/**
 * ExampleShowcase — 실제 생성엔진 산출물(RA/JSA/TBM 전체) 그대로 표시.
 * 요약 없이 모든 항목(위험요인·법적근거·법규준수·보호구·시나리오·이미지) 렌더 → 제품력 증명.
 */
import { useMemo, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  FiFileText, FiGrid, FiClipboard, FiArrowRight, FiCheckCircle,
  FiAlertTriangle, FiChevronDown, FiZap, FiShield, FiBookOpen, FiImage,
} from 'react-icons/fi'
import { cn } from '../../lib/utils'
import {
  loadExamples, matchWork, riskBand, TIER_LABEL, TIER_CLS, parseTagged,
  type ExampleWork, type Ra, type Jsa, type Tbm,
} from './acExamples'
import type { AcLang } from './acI18n'

const SIGNUP_PATH = '/signup'

const T = {
  ko: {
    heading: '가입하면 이런 실제 산출물을 자동으로 받습니다',
    sub: '아래는 우리 AI 엔진이 실제로 생성한 결과물입니다(예시). 가입 후 내 작업·현장 사진에 맞춰 즉시 생성됩니다.',
    basis: (w: string) => `예시 기준: ${w}`,
    tabs: { ra: '위험성평가', jsa: 'JSA (작업안전분석)', tbm: 'TBM (작업 전 회의)' },
    sample: '실제 AI 생성물',
    cta: '내 작업으로 무료 생성하기',
    // RA
    raStatHaz: '위험요인', raStatUnit: '단위작업', raStatLaw: '관련 법규', raCrim: '형사',
    raTable: '위험성평가표(전체)', colUnit: '단위작업', colHazard: '유해·위험요인 / 발생원인',
    colNow: '현재 안전조치', colRisk: '위험도(빈도×강도)', colMeasure: '감소대책', colAfter: '개선 후', colLaw: '법적근거',
    raCompliance: '법규준수 점검(산업안전보건기준규칙)', mustCheck: '필수점검', criminal: '형사처벌', fine: '과태료',
    raCases: '근거가 된 실제 사고사례', hazardLabel: '위험', preventLabel: '예방',
    // JSA
    jsaWork: '작업명', jsaDept: '부서', jsaSup: '관리감독자', jsaWorkers: '작업자',
    jsaStep: '작업단계', jsaHazard: '위험요인', jsaNow: '현재 안전조치', jsaMeasure: '개선조치', jsaRiskBefore: '위험성(전)', jsaRiskAfter: '위험성(후)', jsaPic: '담당자',
    jsaPpe: '법정 보호구(안전인증)', cert: '인증', grade: '등급',
    // TBM
    tbmSummary: '작업 개요', tbmImages: 'AI 생성 안전 포스터 · 4컷 만화', tbmHazards: '핵심 위험요인',
    tbmRules: '안전수칙', tbmLegal: '법적 근거', tbmPpe: '필수 보호구', tbmEmerg: '비상 조치요령',
    tbmScenarios: '사고 시나리오', tbmPrevent: '예방 포인트', riskHigh: '상', riskMid: '중', riskLow: '하',
  },
  en: {
    heading: 'Sign up to auto-generate real outputs like these',
    sub: 'Below are actual outputs generated by our AI engine (sample). After sign-up they are generated for your exact task.',
    basis: (w: string) => `Sample for: ${w}`,
    tabs: { ra: 'Risk Assessment', jsa: 'JSA', tbm: 'TBM' },
    sample: 'Real AI output',
    cta: 'Generate for my task — free',
    raStatHaz: 'Hazards', raStatUnit: 'Unit tasks', raStatLaw: 'Related laws', raCrim: 'Criminal',
    raTable: 'Full risk assessment', colUnit: 'Unit task', colHazard: 'Hazard / Cause',
    colNow: 'Current controls', colRisk: 'Risk (F×S)', colMeasure: 'Controls', colAfter: 'After', colLaw: 'Legal',
    raCompliance: 'Legal compliance (OSH rules)', mustCheck: 'Must-check', criminal: 'Criminal', fine: 'Fine',
    raCases: 'Grounding real accidents', hazardLabel: 'Hazard', preventLabel: 'Prevention',
    jsaWork: 'Work', jsaDept: 'Dept', jsaSup: 'Supervisor', jsaWorkers: 'Workers',
    jsaStep: 'Step', jsaHazard: 'Hazard', jsaNow: 'Current controls', jsaMeasure: 'Improvements', jsaRiskBefore: 'Risk (before)', jsaRiskAfter: 'Risk (after)', jsaPic: 'PIC',
    jsaPpe: 'Legal PPE (certified)', cert: 'Cert', grade: 'Grade',
    tbmSummary: 'Work summary', tbmImages: 'AI safety poster · 4-panel comic', tbmHazards: 'Key hazards',
    tbmRules: 'Safety rules', tbmLegal: 'Legal basis', tbmPpe: 'Required PPE', tbmEmerg: 'Emergency actions',
    tbmScenarios: 'Accident scenarios', tbmPrevent: 'Prevention points', riskHigh: 'High', riskMid: 'Med', riskLow: 'Low',
  },
}

function RiskBadge({ score, label, lang }: { score: number; label?: string; lang: AcLang }) {
  const b = riskBand(score)
  return (
    <span className={cn('inline-flex items-center gap-1 whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-bold', b.cls)}>
      {label || score}<span className="font-medium opacity-75">· {lang === 'en' ? b.en : b.ko}</span>
    </span>
  )
}

// 대책/조치 항목 — 태그 pill + 텍스트를 한 줄(hanging indent)로 깔끔하게.
function MeasureItem({ tierKey, tierLabel, text }: { tierKey: string; tierLabel: string; text: string }) {
  return (
    <li className="flex items-start gap-1.5 leading-snug">
      {tierLabel && (
        <span className={cn('mt-[1px] shrink-0 rounded px-1.5 py-[1px] text-[10px] font-semibold', TIER_CLS[tierKey] || 'bg-white/10 text-ink-muted')}>
          {tierLabel}
        </span>
      )}
      <span className="text-[12.5px] text-ink-muted">{text}</span>
    </li>
  )
}
function TierChips({ items, lang }: { items: { tier: string; measure: string }[]; lang: AcLang }) {
  return (
    <ul className="space-y-1.5">
      {items.map((m, i) => <MeasureItem key={i} tierKey={m.tier} tierLabel={TIER_LABEL[m.tier]?.[lang] || m.tier} text={m.measure} />)}
    </ul>
  )
}
const TAG_KEY: Record<string, string> = {
  '공학적': 'engineering', '관리적': 'administrative', '보호구': 'ppe', '제거': 'elimination', '대체': 'substitution',
  // 영어 번역본 태그 — 색상(tier) 유지
  'Engineering': 'engineering', 'Administrative': 'administrative', 'PPE': 'ppe', 'Elimination': 'elimination', 'Substitution': 'substitution',
}
function TaggedList({ raw }: { raw: string; lang: AcLang }) {
  return (
    <ul className="space-y-1.5">
      {parseTagged(raw).map((s, i) => <MeasureItem key={i} tierKey={TAG_KEY[s.tag] || ''} tierLabel={s.tag} text={s.text} />)}
    </ul>
  )
}

// ── RA (위험성평가 전체) ──────────────────────────────────────────────────────
function RaView({ ra, lang }: { ra: Ra; lang: AcLang }) {
  const t = T[lang]
  const comp = ra.compliance
  return (
    <div className="space-y-6">
      {/* 통계 */}
      <div className="grid grid-cols-3 gap-3">
        <Stat n={ra.hazards.length} label={t.raStatHaz} />
        <Stat n={ra.unit_tasks?.length || new Set(ra.hazards.map((h) => h.unit_task)).size} label={t.raStatUnit} />
        <Stat n={comp?.matched_count || 0} label={t.raStatLaw} sub={comp?.criminal_count ? `${t.raCrim} ${comp.criminal_count}` : undefined} />
      </div>

      {/* 위험성평가표 전체 */}
      <div>
        <SecTitle icon={FiGrid}>{t.raTable}</SecTitle>
        <div className="max-h-[600px] overflow-auto rounded-xl border border-surface-elevated/50">
          <table className="w-full min-w-[1140px] border-collapse text-sm">
            <colgroup>
              <col style={{ width: '11%' }} /><col style={{ width: '22%' }} /><col style={{ width: '12%' }} />
              <col style={{ width: '8%' }} /><col style={{ width: '24%' }} /><col style={{ width: '8%' }} /><col style={{ width: '15%' }} />
            </colgroup>
            <thead className="sticky top-0 z-10 bg-surface-elevated/70 backdrop-blur">
              <tr className="text-left text-[10.5px] font-semibold uppercase tracking-wide text-ink-faint">
                <th className="px-4 py-2.5">{t.colUnit}</th>
                <th className="px-4 py-2.5">{t.colHazard}</th>
                <th className="px-4 py-2.5">{t.colNow}</th>
                <th className="whitespace-nowrap px-2 py-2.5 text-center">{t.colRisk}</th>
                <th className="px-4 py-2.5">{t.colMeasure}</th>
                <th className="whitespace-nowrap px-2 py-2.5 text-center">{t.colAfter}</th>
                <th className="px-4 py-2.5">{t.colLaw}</th>
              </tr>
            </thead>
            <tbody>
              {ra.hazards.map((h, i) => (
                <tr key={i} className={cn('border-t border-surface-elevated/20 align-top transition-colors hover:bg-white/[0.02]', i % 2 === 1 && 'bg-white/[0.015]')}>
                  <td className="px-4 py-3.5 text-[13px] font-medium text-ink">{h.unit_task}</td>
                  <td className="px-4 py-3.5">
                    <div className="text-[13px] font-semibold leading-snug text-ink">{h.hazard}</div>
                    <div className="mt-1 text-[12px] leading-snug text-ink-faint">{h.cause}</div>
                    <span className="mt-1.5 inline-block rounded bg-white/[0.06] px-1.5 py-0.5 text-[10px] text-ink-faint">{h.hazard_category}</span>
                  </td>
                  <td className="px-4 py-3.5 text-[12.5px] leading-snug text-ink-muted">{h.current_controls || '—'}</td>
                  <td className="whitespace-nowrap px-2 py-3.5 text-center">
                    <RiskBadge score={h.frequency * h.severity} lang={lang} />
                    <div className="mt-1 text-[10px] text-ink-faint">{h.frequency}×{h.severity}</div>
                  </td>
                  <td className="px-4 py-3.5"><TierChips items={h.measures || []} lang={lang} /></td>
                  <td className="whitespace-nowrap px-2 py-3.5 text-center"><RiskBadge score={h.residual_frequency * h.residual_severity} lang={lang} /></td>
                  <td className="px-4 py-3.5">
                    {h.legal_basis ? (
                      <div className="text-[12px] leading-snug">
                        <div className="font-semibold text-ink">{h.legal_basis.article_no}</div>
                        <div className="text-ink-faint">{h.legal_basis.article_title}</div>
                        {h.legal_basis.penalty_level === 'criminal' && <span className="mt-1 inline-block rounded bg-red-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-red-400">{t.criminal}</span>}
                      </div>
                    ) : <span className="text-ink-faint">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 법규준수 점검 */}
      {comp && comp.articles?.length > 0 && (
        <div>
          <SecTitle icon={FiBookOpen}>{t.raCompliance}</SecTitle>
          <div className="grid gap-2 sm:grid-cols-2">
            {comp.articles.slice(0, 10).map((a, i) => (
              <div key={i} className="flex items-center justify-between gap-2 rounded-md border border-surface-elevated/40 bg-surface-card px-3 py-2">
                <div className="min-w-0">
                  <span className="text-[13px] font-semibold text-ink">{a.article_no}</span>
                  <span className="ml-1.5 text-[12px] text-ink-muted">{a.article_title}</span>
                </div>
                <div className="flex shrink-0 gap-1">
                  {a.penalty_level === 'criminal' && <span className="rounded bg-red-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-red-400">{t.criminal}</span>}
                  {a.must_check && <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-400">{t.mustCheck}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 근거 사고사례 */}
      {ra.cases?.length > 0 && (
        <div>
          <SecTitle icon={FiAlertTriangle}>{t.raCases}</SecTitle>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {ra.cases.slice(0, 6).map((c, i) => (
              <div key={i} className="rounded-lg border border-surface-elevated/40 bg-surface-card p-3 text-[13px]">
                <p className="text-ink">{c.summary}</p>
                <p className="mt-1.5 text-[12px] text-red-300">⚠ {c.hazard}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ── JSA (전체 · 표 형식 — 우리 시스템 JSA 표 규격) ────────────────────────────
function JsaView({ jsa, lang }: { jsa: Jsa; lang: AcLang }) {
  const t = T[lang]
  const h = jsa.header
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 rounded-lg border border-surface-elevated/40 bg-surface-card p-4 text-[13px] sm:grid-cols-4">
        <KV k={t.jsaWork} v={h.work_name} /><KV k={t.jsaDept} v={h.work_department} />
        <KV k={t.jsaSup} v={h.supervisor} /><KV k={t.jsaWorkers} v={h.workers} />
      </div>

      <div className="max-h-[640px] overflow-auto rounded-xl border border-surface-elevated/50">
        <table className="w-full min-w-[1160px] border-collapse text-sm">
          <colgroup>
            <col style={{ width: '3.5%' }} /><col style={{ width: '13%' }} /><col style={{ width: '18%' }} /><col style={{ width: '21%' }} />
            <col style={{ width: '8%' }} /><col style={{ width: '21%' }} /><col style={{ width: '8%' }} /><col style={{ width: '7.5%' }} />
          </colgroup>
          <thead className="sticky top-0 z-10 bg-surface-elevated/70 backdrop-blur">
            <tr className="text-left text-[10.5px] font-semibold uppercase tracking-wide text-ink-faint">
              <th className="px-2 py-2.5 text-center">#</th>
              <th className="px-4 py-2.5">{t.jsaStep}</th>
              <th className="px-4 py-2.5">{t.jsaHazard}</th>
              <th className="px-4 py-2.5">{t.jsaNow}</th>
              <th className="whitespace-nowrap px-2 py-2.5 text-center">{t.jsaRiskBefore}</th>
              <th className="px-4 py-2.5">{t.jsaMeasure}</th>
              <th className="whitespace-nowrap px-2 py-2.5 text-center">{t.jsaRiskAfter}</th>
              <th className="whitespace-nowrap px-3 py-2.5">{t.jsaPic}</th>
            </tr>
          </thead>
          <tbody>
            {jsa.rows.map((r, i) => (
              <tr key={i} className={cn('border-t border-surface-elevated/20 align-top transition-colors hover:bg-white/[0.02]', i % 2 === 1 && 'bg-white/[0.015]')}>
                <td className="px-2 py-3.5 text-center text-[12px] text-ink-faint">{i + 1}</td>
                <td className="px-4 py-3.5 text-[13px] font-semibold leading-snug text-ink">{r.step}</td>
                <td className="px-4 py-3.5 text-[12.5px] leading-snug text-ink-muted">{r.hazard}</td>
                <td className="px-4 py-3.5"><TaggedList raw={r.safeguards} lang={lang} /></td>
                <td className="whitespace-nowrap px-2 py-3.5 text-center"><RiskBadge score={r.risk_level_value} label={r.risk_level_before} lang={lang} /></td>
                <td className="px-4 py-3.5"><TaggedList raw={r.measures} lang={lang} /></td>
                <td className="whitespace-nowrap px-2 py-3.5 text-center">
                  {r.risk_level_after && r.risk_level_after !== 'N/A'
                    ? <RiskBadge score={(r.frequency_after || 1) * (r.severity_after || 1)} label={r.risk_level_after} lang={lang} />
                    : <span className="text-ink-faint">—</span>}
                </td>
                <td className="whitespace-nowrap px-3 py-3.5 text-[12px] text-ink-faint">{r.pic && r.pic !== 'N/A' ? r.pic : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {jsa.recommended_ppe?.items?.length ? (
        <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/[0.04] p-4">
          <SecTitle icon={FiShield}>{t.jsaPpe}</SecTitle>
          <div className="flex flex-wrap gap-2">
            {jsa.recommended_ppe.items.map((p, i) => (
              <div key={i} className="rounded-md border border-emerald-500/25 bg-surface-card px-3 py-1.5 text-[13px]">
                <span className="font-semibold text-ink">{p.name}</span>
                <span className="ml-1.5 text-[11px] text-emerald-300">[{p.cert}] {t.grade} {p.grade}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  )
}

// ── TBM (전체 + 이미지) ───────────────────────────────────────────────────────
function TbmView({ tbm, lang }: { tbm: Tbm; lang: AcLang }) {
  const t = T[lang]
  const b = tbm.briefing || {}
  const rl = (v: string) => {
    const s = /상|High/i.test(v) ? 15 : /중|Med/i.test(v) ? 9 : 4
    return <RiskBadge score={s} label={v} lang={lang} />
  }
  return (
    <div className="space-y-5">
      {b.work_summary && (
        <div className="rounded-lg border border-amber-500/25 bg-amber-500/[0.06] px-4 py-3">
          <div className="text-[11px] font-semibold uppercase text-amber-400/80">{t.tbmSummary}</div>
          <div className="mt-0.5 text-ink">{b.work_summary}</div>
        </div>
      )}

      {/* AI 이미지 */}
      {(tbm.poster_static || tbm.comic_static) && (
        <div>
          <SecTitle icon={FiImage}>{t.tbmImages}</SecTitle>
          <div className="grid gap-3 sm:grid-cols-2">
            {tbm.poster_static && <img src={tbm.poster_static} alt="safety poster" loading="lazy" className="w-full rounded-lg border border-surface-elevated/40" />}
            {tbm.comic_static && <img src={tbm.comic_static} alt="safety comic" loading="lazy" className="w-full rounded-lg border border-surface-elevated/40" />}
          </div>
        </div>
      )}

      {/* 핵심 위험요인 */}
      {b.hazards?.length ? (
        <div>
          <SecTitle icon={FiAlertTriangle}>{t.tbmHazards}</SecTitle>
          <div className="space-y-2">
            {b.hazards.map((h, i) => (
              <div key={i} className="rounded-lg border border-surface-elevated/40 bg-surface-card p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-ink">{h.no}. {h.hazard}</span>{rl(h.risk_level)}
                </div>
                <p className="mt-1 text-[13px] text-ink-muted">{h.description}</p>
                {h.safety_measures?.length ? (
                  <ul className="mt-1.5 space-y-0.5">
                    {h.safety_measures.map((m, k) => <li key={k} className="flex items-start gap-1.5 text-[12px] text-ink-muted"><FiCheckCircle className="mt-0.5 shrink-0 text-emerald-400" size={12} />{m}</li>)}
                  </ul>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* 수칙·법적근거·보호구·비상 */}
      <div className="grid gap-3 md:grid-cols-2">
        {b.safety_rules?.length ? <ListBox title={t.tbmRules} items={b.safety_rules} color="emerald" /> : null}
        {b.emergency_actions?.length ? <ListBox title={t.tbmEmerg} items={b.emergency_actions} color="red" /> : null}
        {b.ppe_required?.length ? <ListBox title={t.tbmPpe} items={b.ppe_required} color="amber" /> : null}
        {b.legal_basis?.length ? <ListBox title={t.tbmLegal} items={b.legal_basis} color="violet" /> : null}
      </div>

      {/* 시나리오 */}
      {tbm.scenarios?.length ? (
        <div>
          <SecTitle icon={FiFileText}>{t.tbmScenarios}</SecTitle>
          <div className="space-y-3">
            {tbm.scenarios.map((sc, i) => (
              <div key={i} className="rounded-lg border border-surface-elevated/40 bg-surface-card p-4">
                <div className="font-semibold text-amber-300">{sc.title}</div>
                <p className="mt-1 whitespace-pre-line text-[13px] leading-relaxed text-ink-muted">{sc.story}</p>
                {sc.prevention_points?.length ? (
                  <div className="mt-2">
                    <div className="text-[11px] font-semibold text-emerald-300">{t.tbmPrevent}</div>
                    <ul className="mt-1 space-y-0.5">{sc.prevention_points.map((p, k) => <li key={k} className="flex items-start gap-1.5 text-[12px] text-ink-muted"><FiCheckCircle className="mt-0.5 shrink-0 text-emerald-400" size={12} />{p}</li>)}</ul>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  )
}

// ── 공용 소품 ─────────────────────────────────────────────────────────────────
function Stat({ n, label, sub }: { n: number; label: string; sub?: string }) {
  return (
    <div className="rounded-lg border border-surface-elevated/40 bg-surface-card p-3 text-center">
      <div className="text-2xl font-extrabold text-amber-400">{n}</div>
      <div className="text-[12px] text-ink-muted">{label}</div>
      {sub && <div className="text-[10px] text-red-400">{sub}</div>}
    </div>
  )
}
function SecTitle({ icon: Icon, children }: { icon: any; children: ReactNode }) {
  return <div className="mb-2 flex items-center gap-1.5 text-sm font-bold text-ink"><Icon size={15} className="text-amber-400" />{children}</div>
}
function KV({ k, v }: { k: string; v: string }) {
  return <div><span className="text-ink-faint">{k}</span> <span className="ml-1 font-medium text-ink">{v}</span></div>
}
function ListBox({ title, items, color }: { title: string; items: string[]; color: string }) {
  const dot = { emerald: 'bg-emerald-400', red: 'bg-red-400', amber: 'bg-amber-400', violet: 'bg-violet-400' }[color] || 'bg-amber-400'
  return (
    <div className="rounded-lg border border-surface-elevated/40 bg-surface-card p-4">
      <div className="mb-2 text-sm font-semibold text-ink-muted">{title}</div>
      <ul className="space-y-1">{items.map((x, i) => <li key={i} className="flex items-start gap-1.5 text-[13px] text-ink-muted"><span className={cn('mt-1.5 h-1 w-1 shrink-0 rounded-full', dot)} />{x}</li>)}</ul>
    </div>
  )
}

const TABS = [{ key: 'ra', icon: FiGrid }, { key: 'jsa', icon: FiFileText }, { key: 'tbm', icon: FiClipboard }] as const
type TabKey = typeof TABS[number]['key']

export default function ExampleShowcase({ query, lang }: { query: string; lang: AcLang }) {
  const t = T[lang]
  const [tab, setTab] = useState<TabKey>('ra')
  const [pickId, setPickId] = useState<string | null>(null)
  const { data: works } = useQuery({ queryKey: ['acExamples', lang], queryFn: () => loadExamples(lang), staleTime: Infinity })
  const work = useMemo<ExampleWork | null>(() => {
    if (!works?.length) return null
    if (pickId) return works.find((w) => w.id === pickId) || works[0]
    return matchWork(works, query)
  }, [works, query, pickId])
  if (!work) return null

  return (
    <section className="overflow-hidden rounded-2xl border border-surface-elevated/40 bg-surface-card/60">
      <div className="border-b border-surface-elevated/40 bg-gradient-to-br from-amber-500/[0.08] to-transparent px-5 py-5 sm:px-7">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 px-3 py-1 text-xs font-semibold text-amber-400"><FiZap size={12} /> {t.sample}</span>
            <h2 className="mt-2.5 text-xl font-bold text-ink sm:text-2xl">{t.heading}</h2>
            <p className="mt-1.5 max-w-xl text-sm text-ink-muted">{t.sub}</p>
          </div>
          {works && works.length > 1 && (
            <div className="relative">
              <select value={work.id} onChange={(e) => setPickId(e.target.value)}
                className="appearance-none rounded-md border border-surface-elevated/50 bg-surface-deep py-2 pl-3 pr-8 text-sm text-ink focus:border-amber-500/50 focus:outline-none">
                {works.map((w) => <option key={w.id} value={w.id}>{lang === 'en' ? w.label.en : w.label.ko}</option>)}
              </select>
              <FiChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-faint" size={15} />
            </div>
          )}
        </div>
        <div className="mt-2 text-xs text-ink-faint">{t.basis(lang === 'en' ? work.label.en : work.label.ko)}</div>
      </div>

      <div className="flex gap-1 border-b border-surface-elevated/40 px-3 pt-3 sm:px-5">
        {TABS.map(({ key, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)}
            className={cn('flex items-center gap-1.5 rounded-t-md px-3 py-2.5 text-sm font-semibold transition-colors sm:px-4',
              tab === key ? 'bg-surface-elevated/30 text-amber-400' : 'text-ink-faint hover:text-ink-muted')}>
            <Icon size={15} /> <span className="hidden sm:inline">{t.tabs[key]}</span><span className="sm:hidden">{key.toUpperCase()}</span>
          </button>
        ))}
      </div>

      <div className="p-4 sm:p-6">
        <AnimatePresence mode="wait">
          <motion.div key={tab + work.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
            {tab === 'ra' && work.ra && <RaView ra={work.ra} lang={lang} />}
            {tab === 'jsa' && work.jsa && <JsaView jsa={work.jsa} lang={lang} />}
            {tab === 'tbm' && work.tbm && <TbmView tbm={work.tbm} lang={lang} />}
            {tab === 'tbm' && !work.tbm && <div className="py-8 text-center text-sm text-ink-faint">TBM 준비 중…</div>}
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="flex flex-col items-center gap-3 border-t border-surface-elevated/40 bg-surface-deep/30 px-5 py-5 sm:flex-row sm:justify-between sm:px-7">
        <p className="text-sm text-ink-muted">{lang === 'en' ? 'Actual AI output. Get it for your work in seconds.' : '실제 AI 생성물입니다. 내 작업에 맞춰 몇 초 만에 받아보세요.'}</p>
        <Link to={SIGNUP_PATH} className="w-full sm:w-auto">
          <button className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-amber-500 px-6 py-3 text-sm font-semibold text-white hover:bg-amber-400 sm:w-auto">{t.cta} <FiArrowRight /></button>
        </Link>
      </div>
    </section>
  )
}
